import React, { useState } from 'react';
import { useNavigate, useSearchParams, useParams, Outlet } from 'react-router-dom';
import { FaPlus, FaUser, FaEdit, FaTrash, FaFileExcel, FaSave } from 'react-icons/fa';
import { IoIosRefresh } from 'react-icons/io';
import ThreeColumnLayout from '../../layouts/ThreeColumnLayout';
import PageSearch from '../../molecules/PageSearch';
import TableSkeleton from '../../atoms/TableSkeleton';
import FilterSkeleton from '../../atoms/FilterSkeleton';
import UserPageForm from './UserPageForm';
import usersData from '../../../data/users.json';

const UserPageList = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { userId } = useParams();
    const [users, setUsers] = useState(usersData);
    const [loading, setLoading] = useState(false);
    const [hoveredRow, setHoveredRow] = useState<number | null>(null);
    const [showForm, setShowForm] = useState(false);
    const [selectedItems, setSelectedItems] = useState<string[]>([]);

    const searchValue = searchParams.get('search') || '';
    const roleFilter = searchParams.get('role') || '';
    const statusFilter = searchParams.get('status') || '';

    const filterGroups = [
        {
            title: 'Perfil',
            component: (
                <div className="space-y-2">
                    {[
                        { value: '', label: 'Todos os perfis' },
                        { value: 'ADMIN', label: 'Administrador' },
                        { value: 'USER', label: 'Usuário' }
                    ].map((option) => (
                        <button
                            key={option.value}
                            onClick={() => {
                                const newParams = new URLSearchParams(searchParams);
                                if (option.value) {
                                    newParams.set('role', option.value);
                                } else {
                                    newParams.delete('role');
                                }
                                setSearchParams(newParams);
                            }}
                            className={`w-full flex items-center justify-between px-3 py-2 text-sm rounded-lg transition-all duration-200 text-left ${
                                roleFilter === option.value
                                    ? 'bg-blue-600 text-white shadow-md'
                                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700'
                            }`}
                        >
                            <span>{option.label}</span>
                        </button>
                    ))}
                </div>
            )
        },
        {
            title: 'Status',
            component: (
                <div className="flex flex-wrap gap-1">
                    {[
                        { value: '', label: 'Todos', color: 'gray' },
                        { value: 'ACTIVE', label: 'Ativo', color: 'green' },
                        { value: 'INACTIVE', label: 'Inativo', color: 'red' }
                    ].map((option) => (
                        <button
                            key={option.value}
                            onClick={() => {
                                const newParams = new URLSearchParams(searchParams);
                                if (option.value) {
                                    newParams.set('status', option.value);
                                } else {
                                    newParams.delete('status');
                                }
                                setSearchParams(newParams);
                            }}
                            className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                                statusFilter === option.value
                                    ? `bg-${option.color}-600 text-white`
                                    : `bg-${option.color}-100 text-${option.color}-800 hover:bg-${option.color}-200`
                            }`}
                        >
                            {option.label}
                        </button>
                    ))}
                </div>
            )
        }
    ];

    const handleSearch = (value: string) => {
        const newParams = new URLSearchParams(searchParams);
        if (value) {
            newParams.set('search', value);
        } else {
            newParams.delete('search');
        }
        setSearchParams(newParams);
    };

    const handleRowClick = (user: any) => {
        navigate(`/users/${user.id}`);
    };

    const handleSelectItem = (id: string) => {
        setSelectedItems(prev => 
            prev.includes(id) 
                ? prev.filter(item => item !== id)
                : [...prev, id]
        );
    };

    const handleSelectAll = (checked: boolean) => {
        setSelectedItems(checked ? users.map(u => u.id) : []);
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'ACTIVE': return 'bg-green-500';
            case 'INACTIVE': return 'bg-red-500';
            default: return 'bg-gray-500';
        }
    };

    const getRoleLabel = (role: string) => {
        switch (role) {
            case 'ADMIN': return 'Admin';
            case 'USER': return 'Usuário';
            default: return role;
        }
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Filtrar usuários baseado nos parâmetros da URL
    const filteredUsers = users.filter(user => {
        const matchesSearch = !searchValue || 
            user.name.toLowerCase().includes(searchValue.toLowerCase()) ||
            user.email.toLowerCase().includes(searchValue.toLowerCase()) ||
            user.phone.includes(searchValue);
        
        const matchesRole = !roleFilter || user.role === roleFilter;
        const matchesStatus = !statusFilter || user.status === statusFilter;
        
        return matchesSearch && matchesRole && matchesStatus;
    });

    return (
        <>
            {/* Floating Action Button */}
            {!showForm && !userId && (
                <button
                    onClick={() => setShowForm(true)}
                    className="fixed top-[73px] right-[10px] w-10 h-10 rounded-full shadow-xl hover:shadow-2xl transition-all duration-300 z-50 flex items-center justify-center border-2 border-white dark:border-slate-800 bg-blue-600 hover:bg-blue-700 text-white"
                    title="Novo usuário"
                >
                    <FaPlus size={14} />
                </button>
            )}

            <ThreeColumnLayout
                leftSidebarTitle={
                    <div className="flex items-center justify-between w-full">
                        <div className="flex items-center space-x-2">
                            <FaUser className="text-blue-600" size={18} />
                            <span>Usuários</span>
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                            <div>v1.0.0</div>
                            <div className="font-medium text-green-600">DEV</div>
                        </div>
                    </div>
                }
                leftSidebar={
                    loading ? <FilterSkeleton /> : (
                        <div className="p-3 space-y-4">
                            {filterGroups.map((group, index) => (
                                <div key={index}>
                                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        {group.title}
                                    </h3>
                                    {group.component}
                                </div>
                            ))}
                        </div>
                    )
                }
                showRightPanel={!!userId || showForm}
                onCloseRightPanel={() => {
                    setShowForm(false);
                    navigate('/users');
                }}
                rightPanel={
                    showForm ? (
                        <UserPageForm 
                            onClose={() => setShowForm(false)}
                            onSave={(userData) => {
                                console.log('Save user:', userData);
                                setShowForm(false);
                            }}
                        />
                    ) : userId ? (
                        <Outlet />
                    ) : (
                        <div className="p-6 text-center text-gray-500">
                            Selecione um usuário para ver os detalhes
                        </div>
                    )
                }
            >
                <div className="flex flex-col h-full bg-white dark:bg-slate-800 relative overflow-hidden">
                    {/* Fixed Header */}
                    <div className="flex-shrink-0 border-b border-gray-200 dark:border-slate-700 p-3 bg-white dark:bg-slate-800 z-10">
                        <div className="flex items-center justify-between">
                            <div>
                                <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
                                    Usuários
                                </h1>
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                    {filteredUsers.length} usuários encontrados
                                </p>
                            </div>

                            <div className="flex items-center space-x-2">
                                {/* Search */}
                                <div className="w-80">
                                    <PageSearch
                                        value={searchValue}
                                        label="Buscar usuário"
                                        onSearch={handleSearch}
                                        filterCleanVisible={false}
                                        filterCleanEnabled={Boolean(searchValue)}
                                        onFilterClean={() => handleSearch('')}
                                        configureVisible={false}
                                    />
                                </div>

                                {/* Botão Refresh */}
                                <button
                                    onClick={() => setLoading(true)}
                                    disabled={loading}
                                    className="p-2 text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                                    title="Atualizar lista"
                                >
                                    <IoIosRefresh size={18} />
                                </button>

                                {/* Botão Exportar */}
                                <button
                                    className="flex items-center px-3 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                                    title="Exportar para Excel"
                                >
                                    <FaFileExcel className="mr-1" size={14} />
                                    Exportar
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Scrollable Table Container */}
                    <div className="flex-1 overflow-hidden flex flex-col">
                        {loading ? (
                            <div className="flex-1 overflow-y-auto">
                                <TableSkeleton rows={8} />
                            </div>
                        ) : (
                            <>
                                {/* Fixed Table Header */}
                                <div className="flex-shrink-0 bg-gray-50 dark:bg-slate-700 border-b border-gray-200 dark:border-slate-600">
                                    <table className="min-w-full">
                                        <thead>
                                            <tr className="text-xs">
                                                <th className="w-12 px-3 py-2 text-left">
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedItems.length === filteredUsers.length && filteredUsers.length > 0}
                                                        onChange={(e) => handleSelectAll(e.target.checked)}
                                                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                                    />
                                                </th>
                                                <th className="px-3 py-2 text-left text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                                    Nome
                                                </th>
                                                <th className="px-3 py-2 text-left text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                                    Email
                                                </th>
                                                <th className="px-3 py-2 text-left text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                                    Perfil
                                                </th>
                                                <th className="px-3 py-2 text-left text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                                    Status
                                                </th>
                                                <th className="w-32 px-3 py-2 text-left text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                                    Último Login
                                                </th>
                                            </tr>
                                        </thead>
                                    </table>
                                </div>

                                {/* Scrollable Table Body */}
                                <div className="flex-1 overflow-y-auto">
                                    <table className="min-w-full">
                                        <tbody className="bg-white dark:bg-slate-800 divide-y divide-gray-200 dark:divide-slate-700">
                                            {filteredUsers.length === 0 ? (
                                                <tr>
                                                    <td colSpan={6} className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                                                        Nenhum usuário encontrado
                                                    </td>
                                                </tr>
                                            ) : (
                                                filteredUsers.map((user, index) => (
                                                    <tr
                                                        key={user.id}
                                                        className={`text-sm relative group transition-all cursor-pointer ${
                                                            userId === user.id 
                                                                ? 'bg-blue-100 dark:bg-blue-900/30 border-l-4 border-blue-600' 
                                                                : selectedItems.includes(user.id)
                                                                    ? 'bg-blue-50 dark:bg-blue-900/20'
                                                                    : 'hover:bg-gray-50 dark:hover:bg-slate-700 hover:border hover:border-gray-300 dark:hover:border-slate-600'
                                                        }`}
                                                        onMouseEnter={() => setHoveredRow(index)}
                                                        onMouseLeave={() => setHoveredRow(null)}
                                                    >
                                                        <td className="w-12 px-3 py-2">
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedItems.includes(user.id)}
                                                                onChange={() => handleSelectItem(user.id)}
                                                                onClick={(e) => e.stopPropagation()}
                                                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                                            />
                                                        </td>
                                                        <td 
                                                            className="px-3 py-2 text-gray-900 dark:text-white font-medium cursor-pointer"
                                                            onClick={() => handleRowClick(user)}
                                                        >
                                                            {user.name}
                                                        </td>
                                                        <td 
                                                            className="px-3 py-2 text-gray-600 dark:text-gray-400 cursor-pointer"
                                                            onClick={() => handleRowClick(user)}
                                                        >
                                                            {user.email}
                                                        </td>
                                                        <td 
                                                            className="px-3 py-2 cursor-pointer"
                                                            onClick={() => handleRowClick(user)}
                                                        >
                                                            <span className="text-xs text-gray-600 dark:text-gray-400">
                                                                {getRoleLabel(user.role)}
                                                            </span>
                                                        </td>
                                                        <td className="px-3 py-2 relative">
                                                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium text-white ${getStatusColor(user.status)}`}>
                                                                {user.status === 'ACTIVE' ? 'Ativo' : 'Inativo'}
                                                            </span>
                                                        </td>
                                                        <td className="w-32 px-3 py-2 text-xs text-gray-900 dark:text-white cursor-pointer relative">
                                                            {/* Conteúdo normal - visível quando não há hover */}
                                                            <div className={`transition-opacity duration-200 ${hoveredRow === index ? 'opacity-0' : 'opacity-100'}`}>
                                                                {formatDate(user.lastLogin)}
                                                            </div>
                                                            
                                                            {/* Botões de ação - visíveis apenas no hover */}
                                                            {hoveredRow === index && (
                                                                <div className="absolute inset-0 flex items-center justify-center space-x-2 bg-white dark:bg-slate-800">
                                                                    <button 
                                                                        className="p-2 text-blue-600 hover:text-blue-800 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleRowClick(user);
                                                                        }}
                                                                        title="Visualizar detalhes"
                                                                    >
                                                                        <FaEdit size={16} />
                                                                    </button>
                                                                    <button 
                                                                        className="p-2 text-red-600 hover:text-red-800 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            const userToDelete = users.find(u => u.id === user.id);
                                                                            if (window.confirm(`Tem certeza que deseja excluir o usuário ${userToDelete?.name}?`)) {
                                                                                console.log('Delete user:', user.id);
                                                                            }
                                                                        }}
                                                                        title="Excluir usuário"
                                                                    >
                                                                        <FaTrash size={16} />
                                                                    </button>
                                                                </div>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </ThreeColumnLayout>

            {/* Overlay quando formulário está aberto - apenas sobre a área principal */}
            {showForm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-[60]" style={{ right: '600px' }} />
            )}
        </>
    );
};

export default UserPageList;
